import { App as CapacitorApp } from '@capacitor/app'
import { useDecodedAccessToken } from '@cretesuite/api-client-auth'
import { nanoid } from 'nanoid'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useClient } from 'urql'
import { makePhotoRenditions } from '../helpers/imageResize'
import { getIsOnline } from '../helpers/networkStatus'
import { drainPhotoQueue } from '../helpers/photoQueueDrain'
import {
  type QueuedPhotoEntry,
  enqueuePhoto,
  listQueuedPhotos,
  readQueuedPhotoBlobs,
  removeQueuedPhoto,
  writeQueuedPhotoMeta,
} from '../helpers/photoQueueStorage'
import { createPhotoQueueUploader } from '../helpers/photoQueueUploader'
import { useNetwork } from '../hooks/useNetwork'
import { useSession } from '../hooks/useSession'

export type AddPhotoOutcome = 'uploaded' | 'queued'

type PhotoUploadContextValue = {
  /** Queue entries not yet on the server (all orders). */
  pendingPhotos: QueuedPhotoEntry[]
  /**
   * Entries the drain uploaded but no server response has confirmed yet.
   * Kept so the gallery doesn't blink the photo out between "left the
   * queue" and "shows up in the refetched server list".
   */
  recentlyUploaded: QueuedPhotoEntry[]
  /** Bumped after every drain that uploaded something — refetch signal. */
  uploadSerial: number
  /** Drop recentlyUploaded entries the server list now contains. */
  confirmUploaded: (clientIds: string[]) => void
  isPhotoSyncing: boolean
  addPhoto: (
    orderId: number,
    source: Blob,
    takenAtIso?: string | null,
  ) => Promise<AddPhotoOutcome>
  syncPhotos: (opts?: { force?: boolean }) => Promise<void>
  /** Delete a `failed` queued photo from the device (user-confirmed). */
  discardPhoto: (clientId: string) => Promise<boolean>
}

const PhotoUploadContext = createContext<PhotoUploadContextValue>({
  pendingPhotos: [],
  recentlyUploaded: [],
  uploadSerial: 0,
  confirmUploaded: () => {},
  isPhotoSyncing: false,
  // A missing provider must fail loud — resolving 'queued' would tell the
  // crew a photo is durably saved when nothing was written anywhere.
  addPhoto: () =>
    Promise.reject(
      new Error('usePhotoUpload used outside PhotoUploadProvider'),
    ),
  syncPhotos: () => Promise.resolve(),
  discardPhoto: () => Promise.resolve(false),
})

export const usePhotoUpload = () => useContext(PhotoUploadContext)

const CONTENT_TYPE = 'image/jpeg'

// Last decoded session (operator + org). Offline the token can't refresh
// (useDecodedAccessToken returns nothing), but the session hasn't changed —
// without this fallback, offline captures would fail. Logout's
// localStorage.clear() wipes it, so it never outlives the session.
const LAST_SESSION_KEY = 'crew.lastPhotoSession'

type PersistedSession = { operatorId: number; tid: number }

const readPersistedSession = (): PersistedSession | null => {
  const raw = localStorage.getItem(LAST_SESSION_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedSession>
    const { operatorId, tid } = parsed ?? {}
    if (typeof operatorId !== 'number' || typeof tid !== 'number') return null
    return { operatorId, tid }
  } catch {
    return null
  }
}

/**
 * Owns the offline photo-upload queue (see ADR-0006). Photos land in
 * Filesystem `Directory.Data` the moment they're taken and drain to S3 when
 * connectivity allows: on app start, on login, on offline→online, on native
 * resume, right after an enqueue, and on manual sync. The graphcache offline
 * queue can't carry S3 binary PUTs, hence this parallel machinery.
 *
 * The queue deliberately survives logout, so every drain is gated on an
 * authenticated session and entries are stamped with the operator AND org
 * they were taken in — a shared device must never upload operator A's photos
 * as operator B, nor org A's order 42 into org B's order 42.
 *
 * Must render inside the urql `Provider`, `SessionProvider` and
 * `NetworkProvider`.
 */
export const PhotoUploadProvider = ({ children }: React.PropsWithChildren) => {
  const client = useClient()
  const { isOnline } = useNetwork()
  const { loggedIn } = useSession()
  const decodedToken = useDecodedAccessToken()
  const tokenSub = decodedToken?.payload?.sub
  const tokenTid = decodedToken?.payload?.tid
  const tokenSession = useMemo<PersistedSession | null>(() => {
    if (!tokenSub || typeof tokenTid !== 'number') return null
    const operatorId = Number.parseInt(tokenSub, 10)
    return Number.isNaN(operatorId) ? null : { operatorId, tid: tokenTid }
  }, [tokenSub, tokenTid])

  // Remember the decoded session for the offline fallback below
  useEffect(() => {
    if (tokenSession) {
      localStorage.setItem(LAST_SESSION_KEY, JSON.stringify(tokenSession))
    }
  }, [tokenSession])

  // Token first; while logged in but the token is unavailable (offline, or
  // still refreshing on cold start), fall back to the persisted session.
  // Read once per input change: the key only moves in the effect above.
  const session = useMemo(
    () => tokenSession ?? (loggedIn ? readPersistedSession() : null),
    [tokenSession, loggedIn],
  )
  const operatorId = session?.operatorId ?? null
  const tid = session?.tid ?? null

  // loggedIn (refresh token) and the session (access token / persisted)
  // arrive at different moments — only together is the session usable.
  const hasSessionOperator = loggedIn && session !== null

  const [pendingPhotos, setPendingPhotos] = useState<QueuedPhotoEntry[]>([])
  const [recentlyUploaded, setRecentlyUploaded] = useState<QueuedPhotoEntry[]>(
    [],
  )
  const [uploadSerial, setUploadSerial] = useState(0)
  const [isPhotoSyncing, setIsPhotoSyncing] = useState(false)

  const drainInFlight = useRef<Promise<void> | null>(null)
  // Requests that arrived while a drain was running — honored by looping
  // once more instead of being silently coalesced away. Only an explicit
  // force ("Sync now") ignores backoff on that extra pass.
  const forceRequested = useRef(false)
  const rerunRequested = useRef(false)
  // Session state for callbacks that outlive a render.
  const sessionRef = useRef({ hasSessionOperator, operatorId, tid })
  sessionRef.current = { hasSessionOperator, operatorId, tid }

  // Mirrors pendingPhotos so async callbacks read the post-drain queue
  // without an extra disk listing. Only this session's entries (operator +
  // org) are published — other queued photos must not render (they'd show as
  // pending forever; this session's drain will never upload them).
  const pendingRef = useRef<QueuedPhotoEntry[]>([])
  const updatePending = useCallback((entries: QueuedPhotoEntry[]) => {
    const owned = entries.filter(
      (entry) =>
        entry.userId === sessionRef.current.operatorId &&
        entry.tid === sessionRef.current.tid,
    )
    pendingRef.current = owned
    setPendingPhotos(owned)
  }, [])

  const uploader = useMemo(() => createPhotoQueueUploader(client), [client])

  const syncPhotos = useCallback(
    (opts?: { force?: boolean }): Promise<void> => {
      // Without a COMPLETE session (auth + a known operator) a drain would
      // fail into backoff on the login screen, or worse, skip/misattribute
      // the backlog while the access token is still in flight.
      if (!sessionRef.current.hasSessionOperator) return Promise.resolve()

      // Single drain in flight — concurrent triggers share the same run, but
      // are remembered and honored with one more pass (forced only if asked).
      if (drainInFlight.current) {
        rerunRequested.current = true
        if (opts?.force) forceRequested.current = true
        return drainInFlight.current
      }

      let force = opts?.force ?? false
      const run = (async () => {
        setIsPhotoSyncing(true)
        try {
          do {
            rerunRequested.current = false
            forceRequested.current = false
            try {
              const { uploaded, remaining } = await drainPhotoQueue({
                storage: {
                  list: listQueuedPhotos,
                  save: writeQueuedPhotoMeta,
                  remove: removeQueuedPhoto,
                  readBlobs: readQueuedPhotoBlobs,
                },
                uploader,
                isOnline: getIsOnline,
                force,
                ownerId: sessionRef.current.operatorId,
                ownerTid: sessionRef.current.tid,
              })
              // Retain uploads until a server response confirms them, so
              // galleries never blink the photo out mid-transition.
              if (uploaded.length > 0) {
                setRecentlyUploaded((current) => {
                  const known = new Set(current.map((entry) => entry.clientId))
                  const fresh = uploaded.filter(
                    (entry) => !known.has(entry.clientId),
                  )
                  return fresh.length > 0 ? [...current, ...fresh] : current
                })
                setUploadSerial((serial) => serial + 1)
              }
              updatePending(remaining)
            } catch (error) {
              // Never reject to callers: a drain failure must not read as
              // "your (durably saved) photo failed" or abort a manual sync.
              console.error('[PhotoUploadProvider] drain failed', error)
            }
            // A request that arrived mid-run reruns immediately, picking up
            // photos enqueued after the drain snapshotted the queue.
            force = forceRequested.current
          } while (rerunRequested.current || forceRequested.current)
        } finally {
          // Bookkeeping happens inside the loop; release the guard last so
          // no second drain overlaps the tail of this one.
          setIsPhotoSyncing(false)
          drainInFlight.current = null
        }
      })()
      drainInFlight.current = run
      return run
    },
    [uploader, updatePending],
  )

  const addPhoto = useCallback(
    async (
      orderId: number,
      source: Blob,
      takenAtIso?: string | null,
    ): Promise<AddPhotoOutcome> => {
      const { operatorId: owner, tid: ownerTid } = sessionRef.current
      // Fail closed: an unowned entry could later upload under (and be
      // attributed to) whichever operator/org drains next on a shared device.
      if (owner === null || ownerTid === null) {
        throw new Error('cannot queue a photo without a session operator')
      }
      const { display, thumb } = await makePhotoRenditions(source)
      const entry: QueuedPhotoEntry = {
        clientId: nanoid(),
        orderId,
        contentType: CONTENT_TYPE,
        width: display.width,
        height: display.height,
        // Null for library picks — the real capture time is unknown
        takenAtIso: takenAtIso ?? null,
        enqueuedAtIso: new Date().toISOString(),
        userId: owner,
        tid: ownerTid,
        attempts: 0,
        nextAttemptAtIso: null,
        lastError: null,
        state: 'queued',
      }
      // Enqueue-first: the photo is durable on disk before any network I/O,
      // so online upload is just "enqueue + immediate drain" — one code path.
      await enqueuePhoto(entry, display.blob, thumb.blob)
      updatePending([...pendingRef.current, entry])
      // The new entry is already due; a plain pass keeps the backlog's
      // backoff intact (only "Sync now" forces). If a drain is already
      // running, the capture must not wait for the whole backlog: it asks
      // for a rerun and reports "queued" right away.
      const drainIdle = drainInFlight.current === null
      if (getIsOnline() && drainIdle) {
        await syncPhotos()
      } else if (getIsOnline()) {
        void syncPhotos()
      }
      const stillQueued = pendingRef.current.some(
        (queued) => queued.clientId === entry.clientId,
      )
      return stillQueued ? 'queued' : 'uploaded'
    },
    [syncPhotos, updatePending],
  )

  const discardPhoto = useCallback(
    async (clientId: string): Promise<boolean> => {
      const entry = pendingRef.current.find(
        (queued) => queued.clientId === clientId,
      )
      // Only photos that exhausted their retries can be dropped; anything
      // else is still work in progress and must not be lost.
      if (entry?.state !== 'failed') {
        console.error('[PhotoUploadProvider] refusing to discard', clientId)
        return false
      }
      await removeQueuedPhoto(clientId)
      updatePending(
        pendingRef.current.filter((queued) => queued.clientId !== clientId),
      )
      return true
    },
    [updatePending],
  )

  // Trigger effects fire on their OWN signal only — going through a ref keeps
  // a re-created syncPhotos (new urql client identity) from re-kicking them.
  const syncPhotosRef = useRef(syncPhotos)
  syncPhotosRef.current = syncPhotos

  // App start and login: surface any backlog and try to drain it. Keyed on
  // the COMPLETE session so the drain re-fires when the access token lands
  // after loggedIn (cold start) — otherwise the backlog is skipped with
  // nothing left to retrigger it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the session only
  useEffect(() => {
    if (hasSessionOperator) void syncPhotosRef.current()
  }, [hasSessionOperator])

  // Offline → online transition: drain.
  const wasOnline = useRef(isOnline)
  useEffect(() => {
    if (isOnline && !wasOnline.current) void syncPhotosRef.current()
    wasOnline.current = isOnline
  }, [isOnline])

  // Native resume: connectivity often changed while backgrounded.
  useEffect(() => {
    const subscription = CapacitorApp.addListener('resume', () => {
      void syncPhotosRef.current()
    })
    return () => {
      void subscription.then((sub) => sub.remove())
    }
  }, [])

  const confirmUploaded = useCallback((clientIds: string[]) => {
    if (clientIds.length === 0) return
    setRecentlyUploaded((current) => {
      const confirmed = new Set(clientIds)
      const kept = current.filter((entry) => !confirmed.has(entry.clientId))
      // Same reference when nothing changed — avoids a re-render loop
      return kept.length === current.length ? current : kept
    })
  }, [])

  const value = useMemo(
    () => ({
      pendingPhotos,
      recentlyUploaded,
      uploadSerial,
      confirmUploaded,
      isPhotoSyncing,
      addPhoto,
      syncPhotos,
      discardPhoto,
    }),
    [
      pendingPhotos,
      recentlyUploaded,
      uploadSerial,
      confirmUploaded,
      isPhotoSyncing,
      addPhoto,
      syncPhotos,
      discardPhoto,
    ],
  )

  return (
    <PhotoUploadContext.Provider value={value}>
      {children}
    </PhotoUploadContext.Provider>
  )
}
